// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Rating,
  Typography,
  keyframes,
} from "@mui/material";
import { CustomTextFiled } from "../../../components/Custom/Input";
import styled from "@emotion/styled";
import {
  fetchContractorRatingByIdAction,
  removeContractorRatingNoteAction,
  upsertContractorRatingAction,
} from "../../../store/actions/contractorRatings";
import { getUniversalDateTimeFormat } from "../../../utils/methods/universalDateTimeFormat";
import ContractorRatingNotes from "../../../components/ContractorRating/ContractorRatingNotes";

/** Mirrors CONTRACTOR_RATING_NOTE_MAX_LENGTH in the backend schema. */
const NOTE_MAX_LENGTH = 500;

const bounceAnimation = keyframes`
  0% { transform: scale(0); }
  25% { transform: scale(1.1); }
  50% { transform: scale(0.9); }
  75% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    animation: `${bounceAnimation} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)`,
  },
  "& .MuiDialog-paper": {
    backgroundColor: "black",
    boxShadow: "none",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.secondary.main}`,
  },
}));

const SaveButton = styled(Button)(({ theme }) => ({
  color: "white",
  backgroundColor: theme.palette.secondary.main,
  borderRadius: "8px",
  border: "none",
  boxShadow:
    "0px 30px 80px 0px rgba(255, 0, 255, 0.30), 0px 0px 20px 0px rgba(0, 0, 0, 0.60) inset",
  "&:hover": {
    boxShadow:
      "0px 30px 80px 0px rgba(255, 0, 255, 0.30), 0px 0px 20px 0px rgba(0, 0, 0, 0.60) inset",
  },
}));

const CancelButton = styled(Button)(() => ({
  color: "white",
  backgroundColor: "black",
  borderRadius: "8px",
  border: "none",
  boxShadow: "0px 0px 20px 0px gray inset",
  "&:hover": {
    color: "white",
    backgroundColor: "black",
    boxShadow: "0px 0px 10px 0px gray inset",
  },
}));

const SetContractorRatingModal = ({ open, contractor, onClose }) => {
  const dispatch = useDispatch();
  const contractorId = contractor?._id;

  const rating = useSelector(
    (s) => s.contractorRatings?.byContractorId?.[String(contractorId)],
  );
  const upserting = useSelector(
    (s) => s.contractorRatings?.upserting?.[contractorId] ?? false,
  );
  const deletingNote = useSelector(
    (s) => s.contractorRatings?.savingNote?.[contractorId] ?? false,
  );

  const [score, setScore] = useState(0);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open && contractorId) {
      dispatch(fetchContractorRatingByIdAction({ contractorId }));
    }
  }, [open, contractorId, dispatch]);

  useEffect(() => {
    if (rating) setScore(Number(rating.score) || 0);
    else setScore(0);
  }, [rating]);

  // The box is for a NEW comment, never a draft of an old one: it clears on
  // every open so nobody re-posts what they typed for a different contractor.
  useEffect(() => {
    if (open) setNote("");
  }, [open, contractorId]);

  const name = `${contractor?.firstName ?? ""} ${
    contractor?.lastName ?? ""
  }`.trim();

  const trimmedNote = note.trim();
  const noteTooLong = trimmedNote.length > NOTE_MAX_LENGTH;

  const handleSave = () => {
    if (noteTooLong) return;
    dispatch(
      upsertContractorRatingAction({
        contractorId,
        score,
        // Sent in the same request as the score on purpose — one Save button,
        // one write. An empty box only saves the stars.
        note: trimmedNote || undefined,
        successCallback: () => onClose?.(),
      }),
    );
  };

  const handleDeleteNote = (noteId) => {
    dispatch(removeContractorRatingNoteAction({ contractorId, noteId }));
  };

  return (
    <StyledDialog
      open={open}
      onClose={(_e, reason) => {
        if (reason !== "backdropClick") onClose?.();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h5" color="secondary">
          Internal Rating
        </Typography>
        {name && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            py: 1,
            alignItems: "flex-start",
          }}
        >
          <Rating
            value={score}
            precision={0.5}
            size="large"
            onChange={(_e, v) => setScore(v ?? 0)}
            sx={{
              "& .MuiRating-iconEmpty": { color: "rgba(255,255,255,0.35)" },
            }}
          />
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {rating?.updatedBy?.name ? (
              <>
                Last set by <b>{rating.updatedBy.name}</b>
                {rating.updatedBy.userRole
                  ? ` (${rating.updatedBy.userRole})`
                  : ""}
                {rating.updatedAt
                  ? ` on ${getUniversalDateTimeFormat({
                      dateTime: rating.updatedAt,
                    })}`
                  : ""}
              </>
            ) : (
              "Not rated yet."
            )}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.55 }}>
            This rating is internal — contractors cannot see it.
          </Typography>

          <Divider sx={{ width: "100%", borderColor: "rgba(255,255,255,0.15)" }} />

          {/* CustomTextFiled, not a bare TextField: this app runs the DEFAULT
              (light) MUI palette, so an input that does not declare a colour
              types in `rgba(0,0,0,0.87)` — black text on this black dialog.
              The shared component is what every other modal here uses, and it
              forces the value to white. */}
          <CustomTextFiled
            label="Add a note"
            placeholder="Short note about this contractor…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            size="small"
            // Hard stop at the schema limit: the backend 400s past it, and a
            // rejected save would also drop the star change made alongside it.
            inputProps={{ maxLength: NOTE_MAX_LENGTH, "aria-label": "Add a note" }}
            error={noteTooLong}
            helperText={`${trimmedNote.length}/${NOTE_MAX_LENGTH} — saved with your name and the date. Notes cannot be edited afterwards.`}
          />

          <Box sx={{ width: "100%" }}>
            <Typography
              variant="body2"
              color="secondary"
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              Notes
            </Typography>
            <ContractorRatingNotes
              notes={rating?.notes}
              allowDelete
              deleting={deletingNote}
              onDelete={handleDeleteNote}
              emptyLabel="No notes yet."
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} disabled={upserting}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} disabled={upserting}>
          {upserting ? <CircularProgress size={18} /> : "Save"}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default SetContractorRatingModal;
